import React, { useState } from 'react';
import { MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import authService from '../services/authService';
import usePlaylistSearch from '../hooks/usePlaylistSearch';
import PlaylistCard from './PlaylistCard';

function SpotifyPlaylistSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const {
    playlists,
    loading,
    error,
    hasMore,
    total,
    search,
    loadMore
  } = usePlaylistSearch();

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '40px 20px',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    },
    pageTitle: {
      fontSize: '32px',
      fontWeight: 'bold',
      color: '#333',
      marginBottom: '30px',
      textAlign: 'center'
    },
    header: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: '40px'
    },
    logo: {
      maxWidth: '200px',
      height: 'auto',
      marginBottom: '20px'
    },
    searchContainer: {
      display: 'flex',
      gap: '10px',
      width: '100%',
      maxWidth: '600px',
      margin: '0 auto',
      flexDirection: 'column',
      alignItems: 'center'
    },
    searchInputContainer: {
      display: 'flex',
      gap: '10px',
      width: '100%'
    },
    searchInput: {
      flex: 1,
      padding: '12px 20px',
      fontSize: '16px',
      border: '1px solid #ddd',
      borderRadius: '25px',
      outline: 'none'
    },
    searchButton: {
      backgroundColor: '#9B1C1F',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '0 25px',
      fontSize: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    platformSelector: {
      display: 'flex',
      gap: '10px',
      marginTop: '10px'
    },
    platformButton: {
      padding: '8px 16px',
      borderRadius: '20px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s'
    },
    button: {
      backgroundColor: '#9B1C1F',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '12px 25px',
      fontSize: '16px',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none'
    },
    results: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '10px',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    resultText: {
      color: '#666'
    },
    exportButton: {
      backgroundColor: '#333',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '14px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '20px',
      padding: '20px 0'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s'
    },
    cardImage: {
      width: '100%',
      height: '200px',
      objectFit: 'cover'
    },
    cardContent: {
      padding: '20px'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '10px'
    },
    cardSubtitle: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '10px'
    },
    cardDescription: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '15px'
    },
    stats: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '15px',
      fontSize: '14px',
      color: '#666'
    },
    loadMoreButton: {
      backgroundColor: '#9B1C1F',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '12px 25px',
      fontSize: '16px',
      cursor: 'pointer',
      margin: '10px'
    }
  };

  const handleLogin = () => {
    window.location.href = authService.getLoginUrl();
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) return;
    search(searchTerm, selectedPlatform);
  };

  const getPlatformButtonStyle = (platform) => ({
    ...styles.platformButton,
    backgroundColor: selectedPlatform === platform ? '#9B1C1F' : '#e0e0e0',
    color: selectedPlatform === platform ? 'white' : '#333'
  });

  const handlePlatformSelect = (platform) => {
    setSelectedPlatform(platform);
    if (searchTerm.trim()) {
      search(searchTerm, platform);
    }
  };

  const exportToCSV = () => {
    const filteredPlaylists = {
      spotify: playlists.spotify.filter(playlist => playlist && playlist.description && playlist.description.trim() !== ''),
      deezer: playlists.deezer.filter(playlist => playlist && playlist.description && playlist.description.trim() !== '')
    };

    const allPlaylists = [...filteredPlaylists.spotify, ...filteredPlaylists.deezer];
    const headers = [
      'Plateforme',
      'Nom de la playlist',
      'Créateur',
      'Description',
      'Nombre de titres',
      'Nombre de followers',
      'Lien'
    ];

    const data = allPlaylists.map(playlist => [
      playlist.platform,
      `"${playlist.name.replace(/"/g, '""')}"`,
      `"${(playlist.owner?.display_name || 'Inconnu').replace(/"/g, '""')}"`,
      `"${(playlist.description || 'Aucune description').replace(/"/g, '""')}"`,
      playlist.tracks?.total || 0,
      playlist.followers?.total || 0,
      playlist.external_urls?.spotify || playlist.external_urls?.deezer || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...data.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `playlists_${searchTerm.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getTotalResults = () => {
    const filteredCount = {
      spotify: playlists.spotify.filter(playlist => playlist && playlist.description && playlist.description.trim() !== '').length,
      deezer: playlists.deezer.filter(playlist => playlist && playlist.description && playlist.description.trim() !== '').length
    };

    if (selectedPlatform === 'all') {
      return filteredCount.spotify + filteredCount.deezer;
    }
    return filteredCount[selectedPlatform];
  };

  if (!authService.isAuthenticated()) {
    return (
      <div style={styles.container}>
        <h1 style={styles.pageTitle}>Moteur de recherche de playlistes</h1>
        <div style={styles.header}>
          <img 
            src={`${import.meta.env.BASE_URL}inouie_logo.png`}
            alt="Inouie Distribution"
            style={styles.logo}
          />
          <button style={styles.button} onClick={handleLogin}>
            Se connecter avec Spotify
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Moteur de recherche de playlistes</h1>
      <div style={styles.header}>
        <img 
          src={`${import.meta.env.BASE_URL}inouie_logo.png`}
          alt="Inouie Distribution"
          style={styles.logo}
        />
        <div style={styles.searchContainer}>
          <div style={styles.searchInputContainer}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Rechercher des playlists..."
              style={styles.searchInput}
            />
            <button 
              onClick={handleSearch} 
              disabled={loading.spotify || loading.deezer}
              style={styles.searchButton}
            >
              <MagnifyingGlass size={20} />
              {(loading.spotify || loading.deezer) ? 'Recherche...' : 'Rechercher'}
            </button>
          </div>
          <div style={styles.platformSelector}>
            <button 
              onClick={() => handlePlatformSelect('all')}
              style={getPlatformButtonStyle('all')}
            >
              Toutes les plateformes
            </button>
            <button 
              onClick={() => handlePlatformSelect('spotify')}
              style={getPlatformButtonStyle('spotify')}
            >
              Spotify
            </button>
            <button 
              onClick={() => handlePlatformSelect('deezer')}
              style={getPlatformButtonStyle('deezer')}
            >
              Deezer
            </button>
          </div>
        </div>
      </div>

      {(error.spotify || error.deezer) && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#dc2626',
          padding: '15px',
          borderRadius: '10px',
          marginBottom: '20px'
        }}>
          {error.spotify && <div>Erreur Spotify: {error.spotify}</div>}
          {error.deezer && <div>Erreur Deezer: {error.deezer}</div>}
        </div>
      )}

      {(playlists.spotify.length > 0 || playlists.deezer.length > 0) && (
        <div style={styles.results}>
          <span style={styles.resultText}>
            Affichage de {getTotalResults()} playlists avec description
          </span>
          <button onClick={exportToCSV} style={styles.exportButton}>
            <DownloadSimple size={20} />
            Exporter en CSV
          </button>
        </div>
      )}

      <div style={styles.grid}>
        {(selectedPlatform === 'all' || selectedPlatform === 'spotify') &&
          playlists.spotify
            .filter(playlist => playlist && playlist.description && playlist.description.trim() !== '')
            .map((playlist) => (
              <PlaylistCard 
                key={`spotify-${playlist.id}`} 
                playlist={playlist} 
                styles={styles} 
              />
            ))}
        
        {(selectedPlatform === 'all' || selectedPlatform === 'deezer') &&
          playlists.deezer
            .filter(playlist => playlist && playlist.description && playlist.description.trim() !== '')
            .map((playlist) => (
              <PlaylistCard 
                key={`deezer-${playlist.id}`} 
                playlist={playlist} 
                styles={styles} 
              />
            ))}
      </div>

      {(!loading.spotify && !loading.deezer && getTotalResults() === 0 && searchTerm) && (
        <p style={{ textAlign: 'center', marginTop: '30px', color: '#666' }}>
          Aucune playlist avec description trouvée pour "{searchTerm}"
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        {(selectedPlatform === 'all' || selectedPlatform === 'spotify') &&
          hasMore.spotify && playlists.spotify.length > 0 && (
            <button 
              onClick={() => loadMore('spotify')} 
              disabled={loading.spotify}
              style={styles.loadMoreButton}
            >
              {loading.spotify ? 'Chargement...' : 'Charger plus de résultats Spotify'}
            </button>
          )}

        {(selectedPlatform === 'all' || selectedPlatform === 'deezer') &&
          hasMore.deezer && playlists.deezer.length > 0 && (
            <button 
              onClick={() => loadMore('deezer')} 
              disabled={loading.deezer}
              style={styles.loadMoreButton}
            >
              {loading.deezer ? 'Chargement...' : 'Charger plus de résultats Deezer'}
            </button>
          )}
      </div>
    </div>
  );
}

export default SpotifyPlaylistSearch;